'use client'

import * as React from 'react'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { Button } from '@repo/design-system-cf/components/button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { BaseCard } from '@repo/design-system-cf/components/base-card'
import { IconUpload } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { LFS_URL } from '@web/utils/env'
import { useUploadImage } from '@repo/studio-components-image-processing/use-upload-image'
import { ListQueryRootKey, initiateFn } from '@web/gen/service/creativefabrica/backgroundremover/v2'
import { ACCEPTED_IMAGE_EXTENSIONS, isAcceptedBackgroundRemoverFile } from './file-accept'

const EXAMPLE_IDS = ['daniel', 'autumn', 'coffee'] as const

export function BackgroundRemoverTool() {
  return <Home />
}

function Home() {
  const t = useTranslations('pages.background-remover')
  const locale = useLocale()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [file, setFile] = React.useState<File | null>(null)
  const [errorKey, setErrorKey] = React.useState<'invalid_type' | null>(null)

  const { uploadMutation } = useUploadImage({
    initiateMutationFn: initiateFn,
    listQueryKey: ListQueryRootKey,
    options: {
      onSuccess(result) {
        if (result.id) {
          window.location.assign(
            addOrReplaceLocaleInUrl(`${AppRoutes.TOOLS_BACKGROUND_REMOVER}${result.id}`, locale),
          )
        }
      },
    },
  })

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextFile = event.target.files?.[0]
    if (!nextFile) {
      return
    }
    if (!isAcceptedBackgroundRemoverFile(nextFile)) {
      setFile(null)
      setErrorKey('invalid_type')
      return
    }
    setErrorKey(null)
    setFile(nextFile)
  }

  function handleSubmit() {
    if (!isLoggedIn) {
      openModal('login')
      return
    }
    if (!file) {
      inputRef.current?.click()
      return
    }
    uploadMutation.mutate({ files: [file] })
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 overflow-y-auto p-4 lg:flex-row lg:gap-16 lg:px-12">
      <div className="flex w-full max-w-96 flex-col items-center gap-2 text-center lg:items-start lg:text-left">
        <Typography variant="cf-label-01">{t('kicker')}</Typography>
        <Typography variant="cf-title-07" asChild>
          <h1>{t('title')}</h1>
        </Typography>
        <Typography variant="body-01">{t('description')}</Typography>
      </div>

      <div className="flex w-full max-w-lg flex-col gap-3">
        <BaseCard size="md">
          <div className="flex flex-col gap-4">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_IMAGE_EXTENSIONS}
              className="sr-only"
              onChange={handleFileChange}
            />
            <Button
              variant="secondary"
              iconLeft={<IconUpload className="h-5 w-5" />}
              onClick={() => inputRef.current?.click()}
            >
              {file ? file.name : t('upload_label')}
            </Button>
            <Typography variant="cf-label-01">{t('upload_hint')}</Typography>
            {errorKey ? (
              <Typography variant="body-compact-01" className="text-cf-red-800">
                {t(errorKey)}
              </Typography>
            ) : null}
            {uploadMutation.isError ? (
              <Typography variant="body-compact-01" className="text-cf-red-800">
                {t('failed')}
              </Typography>
            ) : null}
            <Button
              variant="primary"
              loading={uploadMutation.isPending}
              loadingText={t('uploading')}
              onClick={handleSubmit}
            >
              {t('remove_background')}
            </Button>
          </div>
        </BaseCard>

        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <Typography variant="cf-label-02-B">{t('examples_title')}</Typography>
            <Typography variant="cf-label-01">{t('examples_description')}</Typography>
          </div>
          <div className="flex gap-2">
            {EXAMPLE_IDS.map(id => (
              <Link
                key={id}
                href={`${AppRoutes.TOOLS_BACKGROUND_REMOVER}${id}`}
                component="Link"
                className="block size-10 overflow-hidden rounded-2"
              >
                <Image
                  src={`${LFS_URL}/studio/background-remover/examples/${id}_thumb.webp`}
                  alt=""
                  width={40}
                  height={40}
                  className="size-full object-cover"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
